import { useState } from 'react';
import {
  Barcode,
  CheckCircle2,
  Download,
  LoaderCircle,
  ShieldCheck,
} from 'lucide-react';
import { api } from '../lib/api';

type BarcodeResult = {
  previewUrl?: string;
  isbn13: string;
  svgUrl: string;
  svgPath: string;
  widthIn: number;
  heightIn: number;
  widthPx300: number;
  heightPx300: number;
};

export default function IsbnBarcodeCreator({
  projectId,
  currentIsbn,
  onChanged,
}: {
  projectId: string;
  currentIsbn?: string;
  onChanged: () => Promise<void> | void;
}) {
  const [mode, setMode] =
    useState<'kdp-auto' | 'own-isbn'>(
      currentIsbn ? 'own-isbn' : 'kdp-auto'
    );
  const [isbn, setIsbn] = useState(currentIsbn || '');
  const [result, setResult] = useState<BarcodeResult | null>(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const generate = async () => {
    setBusy('generate');
    setMessage('');
    try {
      const { data } = await api.post<BarcodeResult>(
        `/api/projects/${projectId}/isbn-barcode`,
        { isbn }
      );
      setResult(data);
      await onChanged();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'The ISBN-13 could not be validated.';
      setMessage(message);
    } finally {
      setBusy('');
    }
  };

  const saveKdpAuto = async () => {
    setBusy('auto');
    setMessage('');
    try {
      await api.put(`/api/projects/${projectId}/barcode-mode`, {
        barcodeMode: 'kdp-auto',
        isbn13: '',
      });
      setResult(null);
      await onChanged();
      setMessage(
        'KDP barcode mode saved. The cover must keep the barcode area clear.'
      );
    } catch {
      setMessage('Barcode mode could not be saved.');
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="card p-6 sm:p-8">
      <div>
        <span className="eyebrow text-[#ef5b3e]">ISBN & BARCODE</span>
        <h2 className="mt-2 text-3xl font-black">
          Use your ISBN or reserve KDP's barcode area.
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-black/50">
          This tool validates and renders an ISBN-13 you already own or received.
          It does not issue or invent ISBN numbers.
        </p>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <button
          type="button"
          onClick={() => setMode('kdp-auto')}
          className={`rounded-2xl border p-5 text-left ${
            mode === 'kdp-auto'
              ? 'border-[#ef5b3e] bg-[#fff0eb]'
              : 'border-black/8 bg-white'
          }`}
        >
          <ShieldCheck size={22} />
          <div className="mt-3 font-black">Let KDP place the barcode</div>
          <p className="mt-2 text-sm text-black/45">
            Keep the back-cover barcode zone clear and upload the cover without
            your own barcode.
          </p>
        </button>

        <button
          type="button"
          onClick={() => setMode('own-isbn')}
          className={`rounded-2xl border p-5 text-left ${
            mode === 'own-isbn'
              ? 'border-[#ef5b3e] bg-[#fff0eb]'
              : 'border-black/8 bg-white'
          }`}
        >
          <Barcode size={22} />
          <div className="mt-3 font-black">Generate my ISBN barcode</div>
          <p className="mt-2 text-sm text-black/45">
            Create print assets from a valid ISBN-13 and place them on the
            full-wrap cover.
          </p>
        </button>
      </div>

      {mode === 'kdp-auto' ? (
        <div className="mt-6">
          <button
            onClick={saveKdpAuto}
            disabled={busy === 'auto'}
            className="primary"
          >
            {busy === 'auto' ? (
              <LoaderCircle size={17} className="animate-spin" />
            ) : (
              <CheckCircle2 size={17} />
            )}
            Save KDP barcode mode
          </button>
        </div>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_380px]">
          <div className="card p-5">
            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-[.12em] text-black/45">
                ISBN-13
              </span>
              <input
                value={isbn}
                onChange={event => setIsbn(event.target.value)}
                placeholder="978-1-234567-89-7"
              />
            </label>
            <p className="mt-3 text-xs leading-relaxed text-black/40">
              Spaces and hyphens are accepted. The tool checks the ISBN-13 check
              digit before creating a barcode.
            </p>

            <button
              onClick={generate}
              disabled={busy === 'generate'}
              className="primary mt-5"
            >
              {busy === 'generate' ? (
                <LoaderCircle size={17} className="animate-spin" />
              ) : (
                <Barcode size={17} />
              )}
              {busy === 'generate' ? 'Generating…' : 'Generate barcode'}
            </button>
          </div>

          <aside className="card p-5">
            {!result ? (
              <div className="py-10 text-center text-sm text-black/40">
                Barcode preview will appear here.
              </div>
            ) : (
              <>
                <img
                  src={result.previewUrl || result.svgUrl}
                  alt={`ISBN barcode ${result.isbn13}`}
                  className="w-full border border-black/10 bg-white p-2"
                />
                <div className="mt-4 text-center font-black">
                  {result.isbn13}
                </div>
                <div className="mt-1 text-center text-xs text-black/40">
                  {result.widthIn} × {result.heightIn} in · {result.widthPx300} ×{' '}
                  {result.heightPx300}px at 300 PPI
                </div>
                <a
                  href={result.svgUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="secondary mt-4 w-full"
                >
                  <Download size={15} /> SVG print barcode
                </a>
                <p className="mt-3 text-xs text-black/40">
                  The cover renderer draws this barcode as vectors, so no raster
                  upscaling is needed for the print PDF.
                </p>
              </>
            )}
          </aside>
        </div>
      )}

      {message && (
        <div className="mt-5 rounded-2xl bg-[#f1eee6] p-4 text-sm text-black/60">
          {message}
        </div>
      )}
    </section>
  );
}
